<template>
  <div class="slider-container">
    <h2 class="title">Slider Düzenleme</h2>

    <!-- Aktif Slider Sayısı -->
    <p class="active-count">
      Yayında olan slider sayısı: <strong>{{ activeSliderCount }}</strong> / 5
    </p>

    <div class="slider-grid">
      <!-- Form -->
      <form @submit.prevent="isEditing ? updateSlider() : addSlider()" class="slider-form">
        <!-- Önizleme (eğer image varsa) -->
        <div v-if="form.image">
          <img :src="getImageUrl(form.image)" alt="Önizleme" style="max-width: 100%; margin-bottom: 10px; border-radius: 8px;" />
        </div>

        <!-- Zorunlu alanlar -->
        <input v-model="form.image" type="text" placeholder="Resim URL" class="form-input" required />
        <input v-model="form.link" type="text" placeholder="Tıklanınca gidilecek URL" class="form-input" />
        <input v-model="form.title" type="text" placeholder="Başlık" class="form-input" required />
        <input v-model="form.category" type="text" placeholder="Kategori" class="form-input" />
        <input type="file" @change="handleFileUpload" class="form-input" />

        <!-- Renk seçimleri -->
        <label>
          Başlık Rengi:
          <input v-model="form.title_color" type="color" class="form-input" />
        </label>
        <label>
          Açıklama Rengi:
          <input v-model="form.desc_color" type="color" class="form-input" />
        </label>

        <!-- Açıklama alanı zorunlu -->
        <textarea v-model="form.description" rows="3" placeholder="Açıklama" class="form-textarea" required></textarea>

        <!-- Yayın durumu + buton -->
        <div class="form-footer">
          <label>
            <input type="checkbox" v-model="form.status" /> Yayında
          </label>
          <button type="submit" class="btn btn-green">
            {{ isEditing ? 'Güncelle' : 'Ekle' }}
          </button>
        </div>
      </form>

      <!-- Kartlar -->
 
      <!-- Sıralanabilir Kartlar -->
      <draggable
        v-model="sliders"
        item-key="id"
        class="slider-cards"
        item-class="slider-card"
        ghost-class="dragging"
        @end="onSortEnd"
      >
        <template #item="{ element: slide }">
          <div
            class="slider-card"
            :class="slide.status ? 'active' : 'inactive'"
          >
            <div class="status-ribbon" v-if="slide.status">✔ Aktif</div>
            <img :src="getImageUrl(slide.image)" class="slider-image" />
            <h3>{{ slide.title }}</h3>
            <p>{{ slide.description }}</p>
            <p class="category">Kategori: {{ slide.category }}</p>
            <p class="status" :class="slide.status ? 'active' : 'inactive'">
              Yayın: {{ slide.status ? 'Aktif' : 'Pasif' }}
            </p>
            <div class="actions">
              <button @click="editSlider(slide)" class="btn btn-yellow">Düzenle</button>
              <button @click="deleteSlider(slide.id)" class="btn btn-red">Sil</button>
              <button v-if="!slide.status" @click="publishSlider(slide.id)" class="btn btn-green">Yayına Al</button>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- Yayındaki slaytlar -->
    <h3 class="slider-row-title">Yayındaki Slaytlar</h3>
    <div class="slider-scroll-row">
      <div
        v-for="slide in sliders.filter(s => s.status)"
        :key="'row-' + slide.id"
        class="slider-scroll-card"
      >
        <img :src="getImageUrl(slide.image)" />
        <p :style="{ color: slide.title_color || '#fff' }">{{ slide.title }}</p>
        <p :style="{ color: slide.desc_color || '#ccc' }">{{ slide.description }}</p>
      </div>
    </div>
    <p v-if="!sliders.length" class="no-slider">Henüz slider bulunmuyor.</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { getToken } from '@/utils/auth'
import draggable from 'vuedraggable'

const baseURL = process.env.VUE_APP_API_URL

// Slider verileri ve form durumu
const sliders = ref([])
const isEditing = ref(false)

// Aktif slider sayısını hesaplayan computed
const activeSliderCount = computed(() => sliders.value.filter(s => s.status).length)

// Yeni slider form verileri (renk dahil)
const form = ref({
  id: null,
  image: '',
  title: '',
  description: '',
  link: '',
  category: '',
  status: true,
  title_color: '#ffffff',
  desc_color: '#dddddd'
})

// Görsel yolu düzenleme fonksiyonu
const getImageUrl = (path) => {
  if (!path) return ''
  return path.startsWith('/uploads') ? `${baseURL}${path}` : path
}

// Türkçe karakterler için latin1 → utf8 dönüştürücü
const fixEncoding = (text) => {
  try {
    return Buffer.from(text || '', 'latin1').toString('utf8')
  } catch {
    return text
  }
}

// Slider düzenleme butonuna basıldığında formu dolduran fonksiyon
const editSlider = (slide) => {
  form.value = {
    ...slide,
    title_color: slide.title_color || '#ffffff',
    desc_color: slide.desc_color || '#dddddd'
  }
  isEditing.value = true
}

// Slider listesini API'den çeken fonksiyon
const fetchSliders = async () => {
  try {
    const res = await axios.get('/admin/sliders', {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    sliders.value = res.data
  } catch (err) {
    console.error('Sliderlar alınamadı:', err)
  }
}

// Formu sıfırlayan fonksiyon
const resetForm = () => {
  form.value = {
    id: null,
    image: '',
    title: '',
    description: '',
    link: '',
    category: '',
    status: true,
    title_color: '#ffffff',
    desc_color: '#dddddd'
  }
  isEditing.value = false
}

// Yeni slider ekleyen fonksiyon (Türkçe karakter dönüşümü dahil)
const addSlider = async () => {
  try {
    const payload = {
      ...form.value,
      title: fixEncoding(form.value.title),
      description: fixEncoding(form.value.description),
      category: fixEncoding(form.value.category)
    }

    await axios.post('/admin/sliders', payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    resetForm()
    fetchSliders()
  } catch (err) {
    console.error('Ekleme başarısız:', err)
  }
}

// Slider güncelleme fonksiyonu (Türkçe karakter dönüşümü dahil)
const updateSlider = async () => {
  try {
    const payload = {
      ...form.value,
      title: fixEncoding(form.value.title),
      description: fixEncoding(form.value.description),
      category: fixEncoding(form.value.category)
    }

    await axios.put(`/admin/sliders/${form.value.id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    resetForm()
    fetchSliders()
  } catch (err) {
    console.error('Güncelleme başarısız:', err)
  }
}

// Slider silme fonksiyonu
const deleteSlider = async (id) => {
  if (!confirm('Bu slider silinsin mi?')) return
  try {
    await axios.delete(`/admin/sliders/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    fetchSliders()
  } catch (err) {
    console.error('Silme başarısız:', err)
  }
}

// Yayına alma fonksiyonu (title, desc fixli gönderim)
const publishSlider = async (id) => {
  try {
    const slide = sliders.value.find(s => s.id === id)
    if (!slide) return

    const payload = {
      ...slide,
      title: fixEncoding(slide.title),
      description: fixEncoding(slide.description),
      category: fixEncoding(slide.category),
      status: true
    }

    await axios.put(`/admin/sliders/${id}`, payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })

    fetchSliders()
  } catch (err) {
    console.error('Yayına alma başarısız:', err)
  }
}

// Dosya yükleme işlemi
const handleFileUpload = async (e) => {
  const file = e.target.files[0]
  if (!file) return

  const formData = new FormData()
  formData.append('image', file)

  try {
    const res = await axios.post('/admin/sliders/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken()}`
      }
    })
    form.value.image = res.data.imagePath
  } catch (err) {
    console.error('Görsel yüklenemedi:', err)
  }
}
const onSortEnd = async () => {
  try {
    const payload = sliders.value.map((slide, index) => ({
      id: slide.id,
      order: index
    }))

    await axios.post('/admin/sliders/update-order', payload, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })

    // İsteğe bağlı toast veya uyarı verebilirsin
    console.log('Sıralama güncellendi')
  } catch (err) {
    console.error('Sıralama güncellenemedi:', err)
  }
}

// Sayfa yüklendiğinde sliderları getir
onMounted(fetchSliders)
</script>

<style scoped>
.slider-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}
.status-ribbon {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #4CAF50;
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
}
.slider-card {
  position: relative;
}
.slider-card.dragging {
  opacity: 0.5;
  transform: scale(0.98);
  transition: all 0.2s ease;
}
.active-count {
  text-align: center;
  color: #fff;
  margin-bottom: 12px;
  font-size: 15px;
}
.title {
  text-align: center;
  font-size: 28px;
  color: white;
  margin-bottom: 30px;
}
.slider-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
}
.slider-form {
  background-color: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 14px;
  background-color: #fff;
  color: #000;
  box-sizing: border-box;
}
.form-textarea {
  resize: vertical;
  min-height: 80px;
}
.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  font-size: 14px;
  color: white;
}
.slider-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.slider-card {
  background-color: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  color: white;
}
.slider-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 10px;
}
.category {
  font-size: 13px;
  color: #ddd;
}
.status {
  font-size: 13px;
  margin-top: 5px;
}
.status.active {
  color: #4CAF50;
}
.status.inactive {
  color: #f44336;
}
.actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
.btn {
  padding: 6px 12px;
  font-size: 13px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: white;
}
.btn-green {
  background-color: #4CAF50;
}
.btn-green:hover {
  background-color: #43a047;
}
.btn-yellow {
  background-color: #fbc02d;
}
.btn-yellow:hover {
  background-color: #f9a825;
}
.btn-red {
  background-color: #f44336;
}
.btn-red:hover {
  background-color: #e53935;
}
.no-slider {
  text-align: center;
  color: white;
  margin-top: 30px;
}

.slider-row-title {
  color: white;
  margin-top: 40px;
  margin-bottom: 10px;
  font-size: 18px;
}
.slider-scroll-row {
  display: flex;
  overflow-x: auto;
  gap: 12px;
  padding-bottom: 10px;
}
.slider-scroll-card {
  min-width: 200px;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 10px;
  border-radius: 8px;
  flex-shrink: 0;
  color: white;
}
.slider-scroll-card img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 6px;
}
.ghost {
  background: #ffffff22;
  border: 2px dashed #aaa;
  min-height: 120px;
  border-radius: 10px;
}
@media (max-width: 900px) {
  .slider-grid {
    grid-template-columns: 1fr;
  }
  .slider-cards {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 600px) {
  .slider-container {
    padding: 20px 10px;
  }

  .title {
    font-size: 22px;
    margin-bottom: 20px;
  }

  .active-count {
    font-size: 14px;
  }

  .slider-form {
    padding: 16px;
    gap: 10px;
  }

  .form-input,
  .form-textarea {
    font-size: 13px;
    padding: 8px 12px;
  }

  .form-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .slider-card {
    padding: 12px;
  }

  .slider-image {
    height: 100px;
  }

  .slider-scroll-card {
    min-width: 160px;
  }

  .slider-scroll-card img {
    height: 80px;
  }

  .slider-cards {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .btn {
    font-size: 12px;
    padding: 6px 10px;
  }
}

</style>
